import React, { useState, useEffect, useContext } from "react";

import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import { TextElement } from "@/components/ui-kit/TextElement/TextElement";
import SearchContext from "@/context/SearchContext";
import { useNavigation } from "@/hooks/useNavigation";
import { ContentfulProduct } from "@/types/common";
import { LinkedItem } from "@/types/search";
import { GA4 } from "@/utils/ga4Utils";
import { cn } from "@/utils/general";

import LinkedItems from "./LinkedItems";
import QuizCTA from "./QuizCTA";
import ResultCard from "./ResultCard";
import styles from "./SuggestedSearch.module.scss";
import { matchAlgoliaToContentful } from "./utils";

interface SuggestedSearchProps {
  products: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hits: any[];
    queryID?: string;
  };
  articles: LinkedItem[];
  pages: LinkedItem[];
  onClick: (e: React.MouseEvent, item: LinkedItem | ContentfulProduct) => void;
}

const SuggestedSearch: React.FC<SuggestedSearchProps> = ({
  products,
  articles,
  pages,
  onClick,
}) => {
  const { search, searchState, setSearchState, contentfulProducts } = useContext(SearchContext);
  const [isFaqExpanded, setIsFaqExpanded] = useState(false);

  const matchedProducts = matchAlgoliaToContentful(contentfulProducts, products?.hits);
  const isProductResultsFound = Boolean(matchedProducts?.length);
  const navigate = useNavigation();

  useEffect(() => {
    setSearchState("results");
    setIsFaqExpanded(false);
  }, [search, setSearchState]);

  return (
    <div className={styles.suggestedSearch}>
      {articles?.length > 0 && (
        <div className="mb-8 md:mb-[40px] lg:mb-[80px]">
          <LinkedItems
            title="Frequently Asked Questions"
            items={isFaqExpanded ? articles : articles.slice(0, 3)}
            onClick={onClick}
          />
          {articles.length > 3 && !isFaqExpanded && (
            <ButtonNova
              level="Textlink"
              label="View More"
              onClick={(e: { nativeEvent: { stopImmediatePropagation: () => void } }) => {
                setIsFaqExpanded(true);
                e.nativeEvent?.stopImmediatePropagation();
              }}
              className="block view-more-faq-button mt-2 md:mt-4"
            />
          )}
        </div>
      )}

      {isProductResultsFound && (
        <>
          <TextElement
            element="h2"
            text={`Products`}
            className={cn(
              "pb-4 md:pb-2 xl:pb-4 title--xsmall text-master-base-black",
              styles.titleFont
            )}
          />
          <div className="flex gap-2 xl:gap-4 flex-wrap mb-8 md:mb-0">
            {matchedProducts.map((product) =>
              product ? (
                <button
                  key={product?.sku}
                  className={`${styles.suggestedSearchButton} h-auto`}
                  onClick={(event) => {
                    GA4.onSearchClicked({
                      search_term: search,
                      name: "Search",
                      location: "navigation",
                      state: searchState,
                      text: product?.title,
                      type: "Link",
                      index: "Products",
                      eventType: "click",
                      queryID: products?.queryID,
                      objectID: product?.objectID,
                    });
                    onClick(event, product);
                  }}
                  aria-label={product?.title}
                >
                  <ResultCard data={product} onClick={onClick} isSearchResult />
                </button>
              ) : null
            )}
          </div>
        </>
      )}

      <div className={`mb-0 md:mb-[40px] ${isProductResultsFound ? "lg:mb-[80px]" : ""}`}>
        <QuizCTA />
      </div>

      {pages?.length > 0 && (
        <LinkedItems
          title="Resources"
          items={pages}
          onClick={(e, item) => {
            e.preventDefault();
            navigate(item.path || "/");
          }}
        />
      )}
    </div>
  );
};

export default SuggestedSearch;
